"use client";

import { useState, type FormEvent } from "react";
import { useTranslations } from "use-intl";
import { ActionButton, Field, Modal, TextInput } from "@/components/counter";
import { PosApiError } from "@/lib/api/client";
import { setOwnPin } from "@/components/shell/staffPinClient";

const PIN_SHAPE = /^\d{4,6}$/;

/**
 * Sets the signed-in staff member's own PIN — never another staff member's. The first
 * pos-webapp sign-in a cashier needs before a colleague can hand them the till by PIN alone.
 */
export function SetOwnPinDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useTranslations("pinSwitch.setPin");
  const tc = useTranslations("common");

  const [pin, setPin] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | undefined>();
  const [saved, setSaved] = useState(false);

  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    setPin("");
    setConfirm("");
    setProblem(undefined);
    setSaved(false);
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (busy) return;
    if (!PIN_SHAPE.test(pin)) {
      setProblem(t("mismatch"));
      return;
    }
    if (pin !== confirm) {
      setProblem(t("mismatch"));
      return;
    }
    setProblem(undefined);
    setBusy(true);
    try {
      await setOwnPin(pin);
      setSaved(true);
      setPin("");
      setConfirm("");
    } catch (error) {
      setProblem(error instanceof PosApiError ? error.problem.message : t("mismatch"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("title")}
      description={t("body")}
      width="max-w-md"
      footer={
        <>
          <ActionButton onClick={onClose} disabled={busy}>
            {tc("cancel")}
          </ActionButton>
          <ActionButton
            variant="primary"
            busy={busy}
            disabled={pin.length === 0 || confirm.length === 0}
            onClick={() => submit()}
          >
            {t("save")}
          </ActionButton>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" data-testid="set-own-pin-dialog">
        <Field label={t("pin")}>
          {(id) => (
            <TextInput
              id={id}
              data-testid="set-own-pin-input"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              autoFocus
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="min-h-target-lg font-ledger text-2xl tracking-[0.3em]"
            />
          )}
        </Field>
        <Field label={t("confirm")}>
          {(id) => (
            <TextInput
              id={id}
              data-testid="set-own-pin-confirm"
              type="password"
              inputMode="numeric"
              autoComplete="off"
              maxLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 6))}
              className="min-h-target-lg font-ledger text-2xl tracking-[0.3em]"
            />
          )}
        </Field>
        {problem && (
          <p role="alert" data-testid="set-own-pin-problem" className="text-sm font-semibold text-text-error">
            {problem}
          </p>
        )}
        {saved && !problem && (
          <p role="status" data-testid="set-own-pin-saved" className="text-sm font-semibold text-text-strong">
            {t("saved")}
          </p>
        )}
        <input type="submit" hidden />
      </form>
    </Modal>
  );
}
